
import { useProfileData } from "@/hooks/useProfileData";
import { Loader2 } from "lucide-react";
import { ProfileHeader } from "@/components/profile/ProfileHeader";
import { ProfileReviews } from "@/components/profile/ProfileReviews";
import { ProfileFavorites } from "@/components/profile/ProfileFavorites";
import { ProfileImageHandler } from "@/components/profile/ProfileImageHandler";
import { ProfileMobileNav } from "@/components/profile/ProfileMobileNav";
import type { Profile } from "@/types/profile";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";

export default function Profile() {
  const { user, profile, reviews, isLoading, setProfile } = useProfileData();
  const isMobile = useIsMobile();

  useEffect(() => {
    // Update auth metadata when profile changes
    if (profile && user) {
      supabase.auth.updateUser({
        data: {
          username: profile.username,
          display_name: profile.display_name,
          avatar_url: profile.avatar_url
        }
      });
    }
  }, [profile, user]);

  if (!user || !profile) return null;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className={`container mx-auto px-4 py-8 ${isMobile ? 'pb-32' : ''}`}>
      <div className="relative">
        {isMobile && <ProfileMobileNav />}
        
        <ProfileImageHandler 
          user={user}
          onUpdateProfile={(updatedProfile: Profile) => setProfile(updatedProfile)}
        >
          {({ isUpdatingAvatar, handleProfilePictureUpload }) => (
            <ProfileHeader
              username={profile.username || ""}
              displayName={profile.display_name}
              avatarUrl={profile.avatar_url}
              linkOne={profile.link_one}
              linkTwo={profile.link_two}
              isUpdatingAvatar={isUpdatingAvatar}
              onImageSelect={handleProfilePictureUpload}
              verificationStatus={profile.verification_status}
              isCreative={profile.is_creative}
            />
          )}
        </ProfileImageHandler>

        <div className="mt-8 space-y-8">
          <section>
            <ProfileFavorites userId={user.id} />
          </section>

          <section className={isMobile ? 'mb-16' : ''}>
            <ProfileReviews
              reviews={reviews}
              userDetails={{
                username: profile.username,
                avatar_url: profile.avatar_url,
                link_one: profile.link_one,
                link_two: profile.link_two,
                verification_status: profile.verification_status,
                is_creative: profile.is_creative
              }}
            />
          </section>
        </div>
      </div>
    </div>
  );
}
